import { createServerFn } from "@tanstack/react-start";
import { slugify } from "./slugify";

// A página sempre abre pelo cache. A atualização mensal evita que uma visita
// comum dispare novamente a varredura pesada do catálogo externo.
const REFRESH_MS = 30 * 24 * 60 * 60 * 1000;

export type CommunityArtist = {
  slug: string;
  name: string;
  albumsCount: number;
  collectorsCount: number;
  coverUrl: string | null;
  imageUrl: string | null;
};

export type ArtistRelease = {
  title: string;
  year: number | null;
  coverUrl: string | null;
  collectorsCount: number;
  discogsId: string | null;
};


export type ArtistPage = {
  slug: string;
  name: string;
  imageUrl: string | null;
  bio: string | null;
  bioSource: string | null;
  spotifyUrl: string | null;
  instagramUrl: string | null;
  youtubeUrl: string | null;
  websiteUrl: string | null;
  albumsCount: number;
  collectorsCount: number;
  mostPlayed: { title: string; year: number | null; cover: string | null; url: string | null } | null;
  mostCollected: { title: string; cover: string | null; collectorsCount: number } | null;
  latestVinyl: { title: string; year: number | null; cover: string | null; discogsId: string | null } | null;
  discography: ArtistRelease[];
  /** A Viny é só vinil: sem edição em vinil a página mostra apenas o aviso. */
  hasVinyl: boolean;
  vinylStatus: "available" | "none" | "unavailable";
  /** Atualiza fontes externas depois que o conteúdo local já apareceu. */
  needsRefresh: boolean;
};

function deslug(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\s+/g, " ").trim();
}

/** "Various", "Unknown Artist" etc. não são artistas — nunca listar. */
function isRealArtist(name: string): boolean {
  const n = name.trim().toLowerCase();
  if (n.length < 2) return false;
  return !/^(various|various artists|varios|vários|v\/a|va|unknown artist|unknown|no artist|soundtrack|trilha sonora)$/.test(
    n,
  );
}


/** Ranking calculado exclusivamente com as coleções da Viny. */
export const getCommunityArtists = createServerFn({ method: "GET" })
  .inputValidator((data?: { limit?: number }) => ({ limit: Math.min(Math.max(data?.limit ?? 24, 1), 100) }))
  .handler(async ({ data }): Promise<CommunityArtist[]> => {
    const { publicClient } = await import("./public-supabase.server");
    const sb = publicClient();
    const { data: rows } = await sb.rpc("artist_community_ranking", { _limit: data.limit });
    const list = ((rows ?? []) as Array<{
      artist: string;
      albums_count: number;
      collectors_count: number;
      cover_url: string | null;
    }>).filter((r) => isRealArtist(String(r.artist ?? "")));
    const slugs = list.map((r) => slugify(r.artist));

    const { data: cached } = await sb.from("artists").select("slug, image_url").in("slug", slugs);
    const images = new Map<string, string | null>(
      (cached ?? []).map((a) => [a.slug, a.image_url ?? null]),
    );

    return list.map((r) => {
      const slug = slugify(r.artist);
      return {
        slug,
        name: r.artist,
        albumsCount: Number(r.albums_count ?? 0),
        collectorsCount: Number(r.collectors_count ?? 0),
        coverUrl: r.cover_url ?? null,
        imageUrl: images.get(slug) ?? null,
      };
    });
  });


/** Busca de artistas: prioriza o que já existe na Viny, depois o Spotify. */
export const searchArtists = createServerFn({ method: "GET" })
  .inputValidator((data: { q: string }) => ({ q: String(data?.q ?? "").trim().slice(0, 80) }))
  .handler(async ({ data }): Promise<CommunityArtist[]> => {
    if (data.q.length < 2) return [];
    const { publicClient } = await import("./public-supabase.server");
    const sb = publicClient();

    const { data: rows } = await sb.rpc("artist_community_ranking", { _limit: 100 });
    const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const needle = norm(data.q);
    const local = ((rows ?? []) as any[])
      .filter((r) => isRealArtist(String(r.artist ?? "")))
      .filter((r) => norm(String(r.artist ?? "")).includes(needle))
      .slice(0, 12)
      .map((r) => ({
        slug: slugify(String(r.artist)),
        name: String(r.artist),
        albumsCount: Number(r.albums_count ?? 0),
        collectorsCount: Number(r.collectors_count ?? 0),
        coverUrl: r.cover_url ?? null,
        imageUrl: null as string | null,
      }));

    // Foto de quem já está no nosso cache, para mostrar o rosto na busca.
    if (local.length) {
      const { data: cachedArtists } = await sb
        .from("artists")
        .select("slug, image_url")
        .in("slug", local.map((l) => l.slug));
      const images = new Map((cachedArtists ?? []).map((a) => [a.slug, a.image_url ?? null]));
      for (const l of local) l.imageUrl = images.get(l.slug) ?? null;
    }

    if (local.length >= 6) return local;

    const { getSpotifyArtistProfile } = await import("./spotify.server");
    const profile = await getSpotifyArtistProfile(data.q);
    if (profile && isRealArtist(profile.name) && !local.some((l) => l.slug === slugify(profile.name))) {
      // Só sugerimos quem tem disco em vinil: aqui o foco é exclusivamente vinil.
      const { artistHasVinyl } = await import("./artists.server");
      // Falha temporária da fonte não pode esconder um artista que tem vinil.
      if ((await artistHasVinyl(profile.name)) !== false) {
        local.push({
          slug: slugify(profile.name),
          name: profile.name,
          albumsCount: 0,
          collectorsCount: 0,
          coverUrl: null,
          imageUrl: profile.image,
        });
      }
    }
    return local;
  });

/** Foto + nome canônico do artista, para mostrar na ficha do disco. */
export const getArtistAvatar = createServerFn({ method: "GET" })
  .inputValidator((data: { name: string }) => ({ name: String(data?.name ?? "").trim().slice(0, 120) }))
  .handler(async ({ data }): Promise<{ slug: string; name: string; imageUrl: string | null } | null> => {
    if (!data.name || !isRealArtist(data.name)) return null;
    const slug = slugify(data.name);
    if (!slug) return null;
    const { publicClient } = await import("./public-supabase.server");
    const sb = publicClient();
    const { data: row } = await sb
      .from("artists")
      .select("slug, name, image_url")
      .eq("slug", slug)
      .maybeSingle();
    if (row?.image_url) return { slug: row.slug, name: row.name, imageUrl: row.image_url };

    const { getSpotifyArtistProfile } = await import("./spotify.server");
    const profile = await getSpotifyArtistProfile(data.name).catch(() => null);
    if (!profile?.image) return row ? { slug: row.slug, name: row.name, imageUrl: null } : null;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("artists").upsert(
      {
        slug,
        name: profile.name || data.name,
        image_url: profile.image,
        spotify_id: profile.id,
        spotify_url: profile.url,
        popularity: profile.popularity,
      },
      { onConflict: "slug" },
    );
    return { slug, name: profile.name || data.name, imageUrl: profile.image };
  });



/** Atualização pesada, disparada pelo cliente sem bloquear a abertura da página. */
export const refreshArtistBySlug = createServerFn({ method: "POST" })
  .inputValidator((data: { slug: string; name?: string }) => ({
    slug: String(data?.slug ?? "").slice(0, 120),
    name: data?.name ? String(data.name).slice(0, 120) : undefined,
  }))
  .handler(async ({ data }): Promise<{ updated: boolean }> => {
    const slug = data.slug;
    if (!slug) return { updated: false };
    const { publicClient } = await import("./public-supabase.server");
    const sb = publicClient();
    const { data: cached } = await sb
      .from("artists")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();
    const stale =
      !cached ||
      !cached.refreshed_at ||
      Date.now() - new Date(cached.refreshed_at).getTime() > REFRESH_MS;
    if (!stale) return { updated: false };

    const name = cached?.name || data.name || deslug(slug);
    const { getSpotifyArtistProfile, getSpotifyArtistTopAlbum } = await import("./spotify.server");
    const { musicbrainzArtist, wikipediaArtistBio, discogsVinylDiscography } = await import(
      "./artists.server"
    );
    const profile = await getSpotifyArtistProfile(name).catch(() => null);
    const canonical = profile?.name || name;
    const [links, topAlbum, vinylResult] = await Promise.all([
      musicbrainzArtist(canonical),
      profile ? getSpotifyArtistTopAlbum(profile.id) : Promise.resolve(null),
      discogsVinylDiscography(canonical),
    ]);
    const bio = await wikipediaArtistBio(links.wikipediaTitle, links.wikipediaLang);
    if (!profile && !bio.bio && vinylResult.releases.length === 0 && !cached) {
      return { updated: false };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: saved } = await supabaseAdmin
      .from("artists")
      .upsert(
        {
          slug,
          name: canonical,
          image_url: profile?.image ?? cached?.image_url ?? null,
          bio: bio.bio ?? cached?.bio ?? null,
          bio_source: bio.source ?? cached?.bio_source ?? null,
          spotify_id: profile?.id ?? cached?.spotify_id ?? null,
          musicbrainz_id: links.musicbrainzId ?? cached?.musicbrainz_id ?? null,
          spotify_url: profile?.url ?? cached?.spotify_url ?? null,
          instagram_url: links.instagramUrl ?? cached?.instagram_url ?? null,
          youtube_url: links.youtubeUrl ?? cached?.youtube_url ?? null,
          website_url: links.websiteUrl ?? cached?.website_url ?? null,
          popularity: profile?.popularity ?? cached?.popularity ?? null,
          top_album: (topAlbum ?? cached?.top_album ?? null) as never,
          refreshed_at: vinylResult.complete ? new Date().toISOString() : null,
        },
        { onConflict: "slug" },
      )
      .select("id")
      .maybeSingle();

    if (saved && vinylResult.releases.length) {
      const { error } = await supabaseAdmin.from("artist_releases").upsert(
        vinylResult.releases.map((release, index) => ({
          artist_id: saved.id,
          title: release.title,
          title_key: release.titleKey,
          year: release.year,
          cover_url: release.coverUrl,
          discogs_id: release.discogsId,
          has_vinyl: true,
          position: index,
        })),
        { onConflict: "artist_id,title_key" },
      );
      if (error) console.error("artist releases cache write failed", error.message);
    }
    return { updated: Boolean(saved) };
  });

/** Leitura rápida: somente cache e discos já cadastrados pela comunidade. */
export const getArtistBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string; name?: string }) => ({
    slug: String(data?.slug ?? "").slice(0, 120),
    name: data?.name ? String(data.name).slice(0, 120) : undefined,
  }))
  .handler(async ({ data }): Promise<ArtistPage | null> => {
    if (!data.slug) return null;
    const { publicClient } = await import("./public-supabase.server");
    const sb = publicClient();
    const { data: row } = await sb.from("artists").select("*").eq("slug", data.slug).maybeSingle();
    const name = row?.name || data.name || deslug(data.slug);

    const releasesRequest = row
      ? sb
        .from("artist_releases")
        .select("title, title_key, year, cover_url, discogs_id")
        .eq("artist_id", row.id)
        .eq("has_vinyl", true)
        .order("year", { ascending: false, nullsFirst: false })
        .limit(200)
      : Promise.resolve({ data: [] });
    const [{ data: rels }, { data: collectorRows }] = await Promise.all([
      releasesRequest,
      sb.rpc("artist_community_releases", { _artist: name }),
    ]);
    const collectors = ((collectorRows ?? []) as any[]).map((r) => ({
      title: String(r.title ?? ""),
      key: String(r.title ?? "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\([^)]*\)/g, " ")
        .replace(/[^a-z0-9]+/g, " ")
        .trim(),
      collectorsCount: Number(r.collectors_count ?? 0),
      albumsCount: Number(r.albums_count ?? 0),
      coverUrl: r.cover_url ?? null,
      year: r.year ? Number(r.year) : null,
      discogsId: r.discogs_id ? String(r.discogs_id) : null,
    }));
    const byKey = new Map(collectors.map((c) => [c.key, c]));
    const merged = new Map<string, ArtistRelease>();
    for (const release of rels ?? []) {
      const community = byKey.get(release.title_key);
      merged.set(release.title_key, {
        title: release.title,
        year: release.year ?? community?.year ?? null,
        coverUrl: release.cover_url ?? community?.coverUrl ?? null,
        collectorsCount: community?.collectorsCount ?? 0,
        discogsId: release.discogs_id ?? community?.discogsId ?? null,
      });
    }
    for (const community of collectors) {
      if (!merged.has(community.key)) {
        merged.set(community.key, {
          title: community.title,
          year: community.year,
          coverUrl: community.coverUrl,
          collectorsCount: community.collectorsCount,
          discogsId: community.discogsId,
        });
      }
    }
    const discography = [...merged.values()].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
    const top = (row?.top_album ?? null) as
      | { title?: string; year?: number | null; cover?: string | null; url?: string | null }
      | null;
    const mostCollected = collectors[0] ?? null;
    const latest = discography.find((release) => release.year) ?? discography[0] ?? null;
    const needsRefresh =
      !row || !row.refreshed_at || Date.now() - new Date(row.refreshed_at).getTime() > REFRESH_MS;

    return {
      slug: row?.slug ?? data.slug,
      name,
      imageUrl: row?.image_url ?? null,
      bio: row?.bio ?? null,
      bioSource: row?.bio_source ?? null,
      spotifyUrl: row?.spotify_url ?? null,
      instagramUrl: row?.instagram_url ?? null,
      youtubeUrl: row?.youtube_url ?? null,
      websiteUrl: row?.website_url ?? null,
      albumsCount: collectors.reduce((s, c) => s + c.albumsCount, 0),
      collectorsCount: mostCollected?.collectorsCount ?? 0,
      mostPlayed: top?.title
        ? {
            title: top.title,
            year: top.year ?? null,
            cover: top.cover ?? null,
            url: top.url ?? null,
          }
        : null,
      mostCollected: mostCollected
        ? {
            title: mostCollected.title,
            cover: mostCollected.coverUrl,
            collectorsCount: mostCollected.collectorsCount,
          }
        : null,
      latestVinyl: latest
        ? {
            title: latest.title,
            year: latest.year,
            cover: latest.coverUrl,
            discogsId: latest.discogsId,
          }
        : null,
      discography,
      hasVinyl: discography.length > 0,
      vinylStatus: discography.length > 0 ? "available" : row?.refreshed_at ? "none" : "unavailable",
      needsRefresh,
    };
  });
